import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, Sparkles, Zap, Shield, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import alternativesHero from "@/assets/alternatives-hero.jpg";

const Alternatives = () => {
  const navigate = useNavigate();

  const competitors = [
    {
      name: "Random.org",
      url: "random.org",
      pros: ["Atmospheric noise RNG", "Established since 1998", "API available"],
      cons: ["Slower performance", "API requires paid subscription", "No mobile app", "Outdated interface"],
      rating: 3.5
    },
    {
      name: "Calculator.net RNG",
      url: "calculator.net",
      pros: ["Free to use", "Multiple calculators"],
      cons: ["Basic Math.random() only", "Not cryptographically secure", "Heavy ads", "Poor mobile experience"],
      rating: 3.0
    },
    {
      name: "Google RNG",
      url: "Google Search",
      pros: ["Quick search result", "Simple interface"],
      cons: ["Very limited features", "No customization", "No history", "Not suitable for serious use"],
      rating: 2.5
    },
    {
      name: "RandomPicker.com",
      url: "randompicker.com",
      pros: ["Simple interface", "Free"],
      cons: ["Basic functionality", "Limited range", "No cryptographic security", "Ads"],
      rating: 3.0
    }
  ];

  const features = [
    {
      feature: "Cryptographically Secure",
      randomgen: true,
      randomorg: true,
      others: false
    },
    {
      feature: "100% Free Forever",
      randomgen: true,
      randomorg: false,
      others: true
    },
    {
      feature: "No Registration Required",
      randomgen: true,
      randomorg: true,
      others: true
    },
    {
      feature: "Mobile Optimized",
      randomgen: true,
      randomorg: false,
      others: false
    },
    {
      feature: "Instant Generation",
      randomgen: true,
      randomorg: false,
      others: true
    },
    {
      feature: "No Ads",
      randomgen: true,
      randomorg: true,
      others: false
    },
    {
      feature: "Modern Interface",
      randomgen: true,
      randomorg: false,
      others: false
    },
    {
      feature: "Statistics & Analytics",
      randomgen: true,
      randomorg: false,
      others: false
    },
    {
      feature: "Multiple Tools (Dice, Coin, Lists)",
      randomgen: true,
      randomorg: true,
      others: false
    },
    {
      feature: "Unlimited Usage",
      randomgen: true,
      randomorg: false,
      others: true
    }
  ];

  return (
    <Layout>
      <SEO
        title="Random.org Alternative - Best Free Random Number Generator 2025"
        description="Looking for Random.org alternatives? RandomGen offers a faster, free, cryptographically secure random number generator with modern features. Compare the best random number generators and see why RandomGen is the top choice."
        keywords="random.org alternative, random number generator, random.org vs, best rng, free random generator, cryptographic rng, random number tool, lottery number generator, random.org comparison"
      />

      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Hero Section */}
        <header className="text-center mb-16">
          <Badge className="mb-4 text-sm px-4 py-1">
            <Sparkles className="w-4 h-4 mr-2 inline" />
            The Modern Alternative
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary-glow to-accent bg-clip-text text-transparent">
            Random.org Alternative: Free & Modern Random Number Generator
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Discover why RandomGen is the best Random.org alternative in 2025. Get cryptographically secure random numbers instantly, completely free, with a modern interface designed for speed and ease of use.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button size="lg" onClick={() => navigate("/")} className="text-lg px-8">
              Try RandomGen Free
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/about")}>
              Learn More
            </Button>
          </div>
        </header>

        {/* Why Choose RandomGen */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Why Choose RandomGen Over Random.org?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 text-center hover:shadow-[var(--shadow-elegant)] transition-[var(--transition-smooth)]">
              <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Lightning Fast</h3>
              <p className="text-muted-foreground">
                Instant number generation using Web Crypto API. No server delays or waiting times like Random.org.
              </p>
            </Card>
            <Card className="p-6 text-center hover:shadow-[var(--shadow-elegant)] transition-[var(--transition-smooth)]">
              <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Cryptographically Secure</h3>
              <p className="text-muted-foreground">
                Uses browser's native CSPRNG for security-grade randomness. Perfect for lottery, contests, and games.
              </p>
            </Card>
            <Card className="p-6 text-center hover:shadow-[var(--shadow-elegant)] transition-[var(--transition-smooth)]">
              <Smartphone className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Mobile Optimized</h3>
              <p className="text-muted-foreground">
                Beautiful, responsive design that works perfectly on all devices. No clunky mobile experience.
              </p>
            </Card>
            <Card className="p-6 text-center hover:shadow-[var(--shadow-elegant)] transition-[var(--transition-smooth)]">
              <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">100% Free Forever</h3>
              <p className="text-muted-foreground">
                Unlimited random number generation with no paid tiers, subscriptions, or API fees. Always free.
              </p>
            </Card>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Feature Comparison: RandomGen vs Random.org vs Others</h2>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-semibold">Feature</th>
                    <th className="text-center p-4 font-semibold text-primary">RandomGen</th>
                    <th className="text-center p-4 font-semibold">Random.org</th>
                    <th className="text-center p-4 font-semibold">Others</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((item, index) => (
                    <tr key={index} className="border-t border-border">
                      <td className="p-4 font-medium">{item.feature}</td>
                      <td className="text-center p-4">
                        {item.randomgen ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="text-center p-4">
                        {item.randomorg ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="text-center p-4">
                        {item.others ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>

        {/* Competitor Overview */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Popular Random Number Generator Alternatives</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {competitors.map((competitor, index) => (
              <Card key={index} className="p-6">
                <h3 className="text-xl font-semibold mb-4">{competitor.name}</h3>
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Pros:</p>
                  <ul className="space-y-1">
                    {competitor.pros.map((pro, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Cons:</p>
                  <ul className="space-y-1">
                    {competitor.cons.map((con, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <span>{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">Rating: {competitor.rating}/5</p>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* What Makes RandomGen Different */}
        <section className="mb-16">
          <Card className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
            <h2 className="text-3xl font-bold mb-6 text-center">What Makes RandomGen the Best Random.org Alternative?</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-muted-foreground mb-4">
                <strong>RandomGen</strong> was built from the ground up as a modern alternative to Random.org and other outdated random number generators. While Random.org pioneered online random number generation in 1998 using atmospheric noise, technology has evolved significantly.
              </p>
              <h3 className="text-xl font-semibold mt-6 mb-3">Cryptographic Security Without Server Dependency</h3>
              <p className="text-muted-foreground mb-4">
                Unlike Random.org which requires server communication for each random number, RandomGen uses your browser's native Web Crypto API (crypto.getRandomValues()). This provides cryptographically secure pseudo-random number generation (CSPRNG) instantly, with no latency or dependency on external servers.
              </p>
              <h3 className="text-xl font-semibold mt-6 mb-3">Completely Free, No Hidden Costs</h3>
              <p className="text-muted-foreground mb-4">
                Random.org limits free usage and requires paid subscriptions for API access. RandomGen is 100% free forever with unlimited usage. No registration, no paywalls, no premium features – everything is available to everyone.
              </p>
              <h3 className="text-xl font-semibold mt-6 mb-3">Modern, Fast, Mobile-First Design</h3>
              <p className="text-muted-foreground mb-4">
                Built with modern web technologies, RandomGen offers a beautiful, intuitive interface that works perfectly on smartphones, tablets, and desktops. No more pinching and zooming on mobile devices.
              </p>
            </div>
          </Card>
        </section>

        {/* Use Cases */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Perfect Random Number Generator For</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">🎰 Lottery & Contests</h3>
              <p className="text-muted-foreground">
                Generate fair, unbiased lottery numbers and contest winners. Cryptographically secure randomness ensures integrity.
              </p>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">🎲 Gaming & Entertainment</h3>
              <p className="text-muted-foreground">
                Perfect for dice rolls, card shuffling, and any game requiring true randomness. Fast and reliable.
              </p>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">📊 Research & Statistics</h3>
              <p className="text-muted-foreground">
                Suitable for statistical sampling, Monte Carlo simulations, and academic research requiring quality randomness.
              </p>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">🏫 Education</h3>
              <p className="text-muted-foreground">
                Teachers and students use RandomGen for probability lessons, experiments, and classroom activities.
              </p>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">🔐 Security Testing</h3>
              <p className="text-muted-foreground">
                Generate secure passwords, test data, and random tokens with cryptographic-grade randomness.
              </p>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-3">🎁 Giveaways & Raffles</h3>
              <p className="text-muted-foreground">
                Run fair giveaways and raffles with transparent, auditable random selection process.
              </p>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-2">Is RandomGen as random as Random.org?</h3>
              <p className="text-muted-foreground">
                Yes! RandomGen uses cryptographically secure pseudo-random number generation (CSPRNG) through the Web Crypto API, which is approved for security-critical applications. While Random.org uses atmospheric noise, both methods produce statistically random results suitable for any application.
              </p>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-2">Why is RandomGen faster than Random.org?</h3>
              <p className="text-muted-foreground">
                RandomGen generates numbers locally in your browser using the Web Crypto API, eliminating server communication delays. Random.org must contact their servers for each generation, adding latency.
              </p>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-2">Do I need to pay for RandomGen?</h3>
              <p className="text-muted-foreground">
                No! RandomGen is completely free with unlimited usage. Unlike Random.org's API which requires paid subscriptions, everything on RandomGen is free forever.
              </p>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-2">Can I use RandomGen for official lottery drawings?</h3>
              <p className="text-muted-foreground">
                Absolutely! RandomGen's cryptographic security makes it suitable for official contests, lotteries, and legal drawings. The randomness quality meets industry standards.
              </p>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <Card className="p-12 bg-gradient-to-r from-primary/10 to-accent/10">
            <h2 className="text-3xl font-bold mb-4">Ready to Try the Best Random.org Alternative?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of users who switched to RandomGen for faster, free, and more reliable random number generation.
            </p>
            <Button size="lg" onClick={() => navigate("/")} className="text-lg px-12">
              Start Using RandomGen Free
            </Button>
          </Card>
        </section>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={alternativesHero} 
            alt="Comparison chart showing Random.org alternatives and best random number generator options with feature comparisons and checkmarks"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Alternatives;
